/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { useVisibilityTracker } from '@coursera/event-pulse/react';

import {
  CourseNameLink,
  EmbeddedPartnerNameTypography,
  TrackedStyledLink,
  getPartnerName,
} from 'bundles/common/lms-authoring/components/EmbeddedPartnerName';
import type { SourceCoursePartners } from 'bundles/common/lms-authoring/types/SourceCoursePartners';

import _t from 'i18n!nls/common';

type Props = {
  partners: SourceCoursePartners;
  courseName?: string;
  originalName?: string;
  className?: string;
  courseSlug?: string | null;
  trimmedAttributionProperties: {
    fullContentLink: string;
    courseId: string;
    itemId: string;
    trimRangeStart: number;
    duration: number | null;
    embeddedSourceCourseId: string;
  };
};

export const TrimmedEmbeddedPartnerName = ({
  partners,
  courseName,
  originalName,
  className,
  courseSlug,
  trimmedAttributionProperties: { fullContentLink, courseId, itemId, trimRangeStart, duration, embeddedSourceCourseId },
}: Props) => {
  const { partnerName } = getPartnerName(partners);

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'video_item_trimmed_attribution',
      },
    }
  );

  if (!originalName) {
    return (
      <div ref={visibilityTrackingRef}>
        <EmbeddedPartnerNameTypography className={className}>
          <FormattedMessage
            message={_t(
              'This video includes a portion of the {lectureLink} from the {courseName} course by {partnerName}'
            )}
            lectureLink={
              <TrackedStyledLink
                href={fullContentLink}
                data-testid="full-video-link"
                trackingData={{
                  button: { name: 'video_trim_learn_view_full_video' },
                  pageSection: {
                    sectionName: 'video_item_trimmed_attribution',
                  },
                  lectureItem: {
                    courseId,
                    itemId,
                    trimRangeStart,
                    videoDuration: duration || undefined,
                    embeddedContentSourceCourseId: embeddedSourceCourseId,
                  },
                }}
              >
                {_t('original lecture')}
              </TrackedStyledLink>
            }
            courseName={<CourseNameLink courseName={courseName} courseSlug={courseSlug} />}
            partnerName={partnerName}
          />
        </EmbeddedPartnerNameTypography>
      </div>
    );
  }

  return (
    <div ref={visibilityTrackingRef}>
      <EmbeddedPartnerNameTypography className={className}>
        <FormattedMessage
          message={_t(
            'This video includes a portion of the original lecture {lectureLink}, from the {courseName} course by {partnerName}'
          )}
          lectureLink={
            <TrackedStyledLink
              href={fullContentLink}
              data-testid="full-video-link"
              trackingData={{
                button: { name: 'video_trim_learn_view_full_video' },
                pageSection: {
                  sectionName: 'video_item_trimmed_attribution',
                },
                lectureItem: {
                  courseId,
                  itemId,
                  trimRangeStart,
                  videoDuration: duration || undefined,
                  embeddedContentSourceCourseId: embeddedSourceCourseId,
                },
              }}
            >
              {originalName}
            </TrackedStyledLink>
          }
          courseName={<CourseNameLink courseName={courseName} courseSlug={courseSlug} />}
          partnerName={partnerName}
        />
      </EmbeddedPartnerNameTypography>
    </div>
  );
};
